import { computed } from "vue";

import { useEmployeesOrganizationTableViewState } from "@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState";
import { useEmployees } from "@/composables/employees/useEmployees";
import { canStartViewAsRef } from "@components/utils/viewAsEligibility";
import { viewAsLdEnabled } from "@components/utils/viewAsFeatureFlag";
import { withoutPreparingEmployeeTableColumns } from "@components/utils/preparingForOrganizationUi";

export function useTableConfig() {
  const viewState = useEmployeesOrganizationTableViewState();
  const employees = useEmployees();
  const accountID = JSON.parse(localStorage.getItem("currentAccountID"));

  const tableParams = computed(() => {
    // Read these inside the computed so a late LD flag / permission update
    // invalidates the config and re-renders EmployeeLineMenu (ER-10317).
    const viewAsFeatureEnabled =
      !!viewState.state.viewAsFeatureEnabled || viewAsLdEnabled.value;
    const canStartViewAs =
      !!viewState.state.canStartViewAs || canStartViewAsRef.value;

    const employeeTableColumns = withoutPreparingEmployeeTableColumns(
      [
        {
          text: "Employee",
          sortable: true,
          value: "first_name",
        },
        {
          text: "Primary",
          sortable: false,
          value: "primary",
          img: "icons/briefcase.svg",
        },
        {
          text: "Prepares for",
          sortable: false,
          value: "prepares",
          img: "icons/preparing.svg",
        },
        {
          text: "Status",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          sortable: false,
          value: "chatter",
        },
        {
          text: "",
          sortable: false,
          value: "actions",
        },
      ],
      [
        {
          field: "",
          component: "EmployeeCard",
          props: (item) => ({
            class: "my-4",
            ...viewState.getEmployeeCardProps(item),
          }),
          events: (item) => ({
            "avatar-click": () => viewState.handleEmployeeAvatarClick(item),
            "update:availability": async (availabilityValue) => {
              await employees.onUpdateAvailability({
                accountId: accountID,
                data: { employee: item, value: availabilityValue },
              });
              await viewState.reloadEmployeesPreservingFilters();
            },
          }),
        },
        {
          field: "",
          component: "EmployeeAssigmentTag",
          props: (item) => ({
            class: "my-4 custom-assign",
            ...viewState.getEmployeeAssigmentTagProps.getBasicInfo(item),
            ...viewState.getEmployeeAssigmentTagProps.getPositionInfo(item),
            ...viewState.getEmployeeAssigmentTagProps.getTypeInfo(item),
          }),
          events: (item) => ({
            onPrimaryAssign: () => {
              employees.setCurrentEmployee(item);
              viewState.openAssignmentWizard();
            },
            onClose: () => {
              employees.setCurrentEmployee(item);
              const primaryPosition = item.primary_position || item.primary_positions?.[0];
              viewState.state.relieveAssignment.currentPosition = primaryPosition || null;
              viewState.state.relieveAssignment.show = true;
              viewState.state.relieveAssignment.type = "primary";
            },
            onHandleApplyDate(date) {
              employees.setCurrentEmployee(item);
              viewState.onApplyDatePrimaryAssignment(date);
            },
            onChatterClick: () => {
              viewState.openEmployeeChatterModal(item);
            },
          }),
        },
        {
          field: "",
          component: "ExEmployeePrepareAssignmentCard",
          props: (item) => ({
            class: "my-4",
            ...viewState.getEmployeePrepareAssignmentCardProps.getOccupiedByInfo(
              item
            ),
            ...viewState.getEmployeePrepareAssignmentCardProps.getEmployeeInfo(
              item
            ),
            ...viewState.getEmployeePrepareAssignmentCardProps.getPositionInfo(
              item
            ),
            ...viewState.getEmployeePrepareAssignmentCardProps.getTypeInfo(
              item
            ),
          }),
          events: (item) => ({
            onPreparePosition: () => {
              employees.setCurrentEmployee(item);
              viewState.openPreparePosition();
            },
            onClose: () => {
              employees.setCurrentEmployee(item);
              viewState.state.relieveAssignment.currentPosition =
                item.preparing_for_positions?.[0] || null;
              viewState.state.relieveAssignment.show = true;
              viewState.state.relieveAssignment.type = "prepare";
            },
            onHandleApplyDate(date) {
              employees.setCurrentEmployee(item);
              viewState.onApplyDatePrepareFor(date);
            },
            onChatterClick: () => {
              viewState.openEmployeeChatterModal(item);
            },
          }),
        },
        {
          field: "",
          component: "EmployeeBadge",
          props: (item) => ({
            ...viewState.getStateBadgeProps(item),
          }),
        },
        {
          field: "",
          component: "ChatterIcon",
          props: (item) => ({
            class: "my-4",
            is_white: false
          }),
          events: (item) => ({
            onClick: () => {
              viewState.openEmployeeChatterModal(item);
            },
          }),
        },
        {
          field: "",
          component: "EmployeeLineMenu",
          props: (item) => {
            const access = item?.access || null;
            const isSuspended = !!(access?.is_suspended);
            const hasAccess = !!(access?.has_access);
            // Match detail view (EmployeeHorizontalSideBar): pass accessStatus and access so suspended employees get same options (e.g. Restore Access, Terminate)
            const accessStatus = viewState.getEmployeeAccess(access);

            return {
              "data-testid": "employee-operations-menu",
              employee: {
                ...item,
                isSuspended,
                hasAccess,
                accessStatus,
                access,
              },
              status: item?.status || "draft",
              menuOpen: false,
              viewAsFeatureEnabled,
              canStartViewAs,
            };
          },
          events: (item) => ({
            action: async ({ type } = {}) => {
              const operation = mapEmployeeLineMenuActionToDropdownOperation(type);
              if (!operation) return;
              await viewState.handleEmployeeDropdownOperation(operation, item);
            },
          }),
        },
      ]
    );

    return {
      headers: employeeTableColumns.headers,
      columns: employeeTableColumns.columns,
      filters: {
        values: [],
        searchText: "",
      },
      hideTopSection: true,
      hideActiveNo: false,
      activeRecords: {
        field: "status",
        value: "active",
      },
      defaultOrderingField: "first_name",
      activeRecordsCount: 0,
      hideGridView: true,
      actionLabel: "Add Employee",
      title: "Employees Organization",
      orderingStorageKey: "employees.ordering",
      showSearchPositionByMultipleFieldBox:
        viewState.state.config.showSearchPositionByMultipleFieldBox,
      searchPositionByMultipleFields:
        viewState.state.config.searchPositionByMultipleFields || [],
      searchInstructions: viewState.state.config.searchInstructions,
      cellContainerMaxWidth: "calc(15vw + 60px)",
      getRowStyle: (item) => ({
        backgroundColor:
          item.position?.data?.status === "active" ? "#ECFDF3" : "#FFFFFF",
      }),
    };
  });
  return {
    tableParams,
  };
}

function mapEmployeeLineMenuActionToDropdownOperation(type) {
  switch (type) {
    case "activate":
      return { value: "activate_action" };
    case "delete":
      return { value: "delete_action" };
    case "terminate":
      return { value: "terminate_action" };
    case "reactivate":
      return { value: "reactivate_action" };
    case "rename":
      return { value: "edit_action" };
    case "giveAccess":
      return { value: "give_access_action" };
    case "restoreAccess":
      return { value: "give_back_access_action" };
    case "suspendAccess":
      return { value: "suspend_access_action" };
    case "changePicture":
      return { value: "change_picture_action" };
    case "resendInvite":
      return { value: "resend_invite_action" };
    case "cancelInvite":
      return { value: "cancel_invite_action" };
    case "archive":
      return { value: "archive_action" };
    case "unarchive":
      return { value: "unarchive_action" };
    case "impersonate":
      return { value: "impersonate_action" };
    case "previewAsEmployee":
      return { value: "preview_as_employee_action" };
    default:
      return null;
  }
}
